import { useCallback, useState } from "react";

import { useLocalStorage } from "app/pages/activation/hooks/helpers/useLocalStorage";
import { allTrackers } from "app/services/track/track";
import { getDateAsText } from "app/UtilitiesAndConstants/UtilityFunctions/timeAndDate";

export type CampaignDismissType = "banner" | "modal";

export interface IUseCampaignDismissParams {
    type: CampaignDismissType;
    isEligible: boolean;
    /** Omit when the banner has no close button — visibility will simply follow `isEligible`. */
    storageKey?: string;
    trackingPrefix?: string;
    ctaType?: string | null;
    /** Only relevant for `type: "modal"` — wrapped with tracking on return. */
    onCtaClick?: () => void;
}

export interface IUseCampaignDismissResult {
    isOpen: boolean;
    onClose: () => void;
    /** Tracking-wrapped CTA click for modals; no-op otherwise. */
    onCtaClick: () => void;
}

const DEFAULT_TRACKING_PREFIX = "campaign";

export const useCampaignDismiss = ({
    type,
    isEligible,
    storageKey,
    trackingPrefix = DEFAULT_TRACKING_PREFIX,
    ctaType,
    onCtaClick,
}: IUseCampaignDismissParams): IUseCampaignDismissResult => {
    const { getLocalStorage, setLocalStorage } = useLocalStorage();

    const todayAsText = getDateAsText();

    const [isDismissed, setIsDismissed] = useState(storageKey ? getLocalStorage(storageKey) === todayAsText : false);

    const isOpen = storageKey ? !isDismissed && isEligible : isEligible;

    const trackingCategory = type === "modal" ? "pop up" : "banner";

    const onClose = useCallback(() => {
        if (!storageKey) {
            return;
        }
        allTrackers.dispatchTrackEvent(trackingCategory, "close", `${trackingPrefix}/${ctaType ?? ""}`);
        setLocalStorage(storageKey, todayAsText);
        setIsDismissed(true);
    }, [ctaType, setLocalStorage, storageKey, todayAsText, trackingCategory, trackingPrefix]);

    const onCtaClickWithTracking = useCallback(() => {
        if (onCtaClick) {
            allTrackers.dispatchTrackEvent("button", "click", `${trackingPrefix}/${ctaType ?? ""}`);
            onCtaClick();
        }
    }, [ctaType, onCtaClick, trackingPrefix]);

    return { isOpen, onClose, onCtaClick: onCtaClickWithTracking };
};
